import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { CellStyle, DiagAutoType, Task, TaskFormatting } from '../api/types';
import { PredecessorEditorModal } from './PredecessorEditorModal';
import { FormatToolbar } from './FormatToolbar';
import { clamp, startHorizontalResize } from '../utils/resize';
import { cellStyleToCss, mergeCellStyle } from '../utils/cellStyle';
import { toSnapshotItem } from '../utils/taskSnapshot';
import { normalizeDateToken, formatDate } from '../utils/dateFormat';
import { beginSave, endSave, markSaved } from '../utils/pendingSave';
import { useAuth } from '../contexts/AuthContext';

type FormatSelection = { type: 'row'; taskId: string } | { type: 'cell'; taskId: string; column: string };

function fromDateInput(value: string): string | null {
  return value ? `${value}T00:00:00.000Z` : null;
}

/**
 * Vrai si indenter une tâche EN DESSOUS de `prevTask` serait refusé côté
 * serveur — uniquement quand `prevTask` devrait devenir un TOUT NOUVEAU
 * groupe (elle n'est ni déjà un groupe, ni déjà indentée dans un groupe
 * existant) ET qu'elle a un prédécesseur (voir TasksService.indent). Si
 * `prevTask` est déjà un groupe, ou déjà elle-même indentée, la tâche
 * rejoint ce groupe existant comme enfant/sœur sans jamais faire de
 * `prevTask` un nouveau groupe — son éventuel prédécesseur n'entre alors
 * pas en jeu.
 */
function indentBlockedReason(prevTask: Task): boolean {
  const prevWouldBecomeNewGroup = !prevTask.isGroup && !prevTask.parentId;
  return prevWouldBecomeNewGroup && !!prevTask.predecessorsText;
}

const DEFAULT_COL_WIDTHS = {
  nameFr: 160,
  nameEn: 160,
  duration: 70,
  start: 140,
  end: 140,
  predecessors: 110,
  progress: 55,
  milestone: 70,
  diagAuto: 80,
  visible: 60,
  master: 70,
};
type ColKey = keyof typeof DEFAULT_COL_WIDTHS;
const COL_WIDTHS_KEY = 'gantt:taskTableColWidths';
const MIN_COL_WIDTH = 40;
const MAX_COL_WIDTH = 500;

function loadColWidths(): Record<ColKey, number> {
  try {
    const saved = localStorage.getItem(COL_WIDTHS_KEY);
    return saved ? { ...DEFAULT_COL_WIDTHS, ...JSON.parse(saved) } : { ...DEFAULT_COL_WIDTHS };
  } catch {
    return { ...DEFAULT_COL_WIDTHS };
  }
}

function ColumnResizeHandle({ onResize }: { onResize: (deltaPx: number) => void }) {
  return (
    <span
      className="col-resize-handle"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        startHorizontalResize(e.clientX, onResize);
      }}
    />
  );
}

const MAX_UNDO_HISTORY = 20;

interface TaskTableProps {
  diagramId: string;
  canWrite: boolean;
  /** Instantané en lecture seule (une version nommée) à afficher à la place du tableau en direct — voir DiagramPage. */
  overrideTasks?: Task[];
}

export function TaskTable({ diagramId, canWrite, overrideTasks }: TaskTableProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dateFormat = user?.dateFormat ?? 'DD-MM-YYYY';
  const [tasks, setTasks] = useState<Task[]>(() => overrideTasks ?? []);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingPredecessorsOf, setEditingPredecessorsOf] = useState<Task | null>(null);
  const [colWidths, setColWidths] = useState<Record<ColKey, number>>(loadColWidths);
  // Incrémenté à chaque rechargement pour forcer le remontage des cellules
  // non contrôlées (defaultValue) même quand la valeur revenue du serveur
  // est identique à l'ancienne — cas d'une saisie invalide rejetée : sans
  // ce nonce, la clé ne changerait pas et le champ garderait à l'écran le
  // texte invalide tapé par l'utilisateur au lieu de revenir à la valeur
  // réellement enregistrée.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [formatSelection, setFormatSelection] = useState<FormatSelection | null>(null);
  const dragIndex = useRef<number | null>(null);
  const historyRef = useRef<Task[][]>([]);
  const [historyLength, setHistoryLength] = useState(0);

  const pushHistory = () => {
    historyRef.current = [...historyRef.current, tasks].slice(-MAX_UNDO_HISTORY);
    setHistoryLength(historyRef.current.length);
  };

  const load = () => {
    if (overrideTasks) return;
    api
      .get<Task[]>(`/diagrams/${diagramId}/tasks`)
      .then((data) => {
        setTasks(data);
        setReloadNonce((n) => n + 1);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };

  useEffect(load, [diagramId]);

  useEffect(() => {
    try {
      localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(colWidths));
    } catch {
      // stockage indisponible : la largeur des colonnes reste simplement non persistée.
    }
  }, [colWidths]);

  const resizeCol = (key: ColKey, delta: number) => {
    setColWidths((prev) => ({ ...prev, [key]: clamp(prev[key] + delta, MIN_COL_WIDTH, MAX_COL_WIDTH) }));
  };

  const patchTask = async (id: string, data: Record<string, unknown>) => {
    pushHistory();
    beginSave();
    try {
      await api.patch<Task>(`/tasks/${id}`, data);
      // Recharge tout le tableau plutôt que de ne remplacer que la ligne
      // éditée : un changement de dates peut se répercuter en cascade sur
      // d'autres tâches (voir TasksService.runCascade) que la seule réponse
      // de ce PATCH ne renverrait jamais.
      load();
      markSaved();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setError(message);
      // Une alerte en plus du message en haut de page : sur une grande table,
      // un rejet (ex. prédécesseur invalide) passerait sinon facilement
      // inaperçu, et la cellule reviendrait à sa valeur précédente sans
      // explication visible.
      alert(message);
      load();
    } finally {
      endSave();
    }
  };

  const handleAdd = async (afterTaskId?: string) => {
    pushHistory();
    await api.post(`/diagrams/${diagramId}/tasks`, afterTaskId ? { afterTaskId } : {});
    load();
    markSaved();
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(t('tasks.deleteConfirm', { count: selected.size }))) return;
    pushHistory();
    await Promise.all([...selected].map((id) => api.delete(`/tasks/${id}`)));
    setSelected(new Set());
    load();
    markSaved();
  };

  const handleDuplicateSelected = async () => {
    if (selected.size === 0) return;
    pushHistory();
    await api.post(`/diagrams/${diagramId}/tasks/duplicate`, { ids: [...selected] });
    setSelected(new Set());
    load();
    markSaved();
  };

  const handleUndo = async () => {
    const previous = historyRef.current[historyRef.current.length - 1];
    if (!previous) return;
    historyRef.current = historyRef.current.slice(0, -1);
    setHistoryLength(historyRef.current.length);
    beginSave();
    try {
      await api.put(`/diagrams/${diagramId}/tasks/restore`, { tasks: previous.map(toSnapshotItem) });
      load();
      markSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      endSave();
    }
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  /** Force le remontage des cellules non contrôlées sans toucher aux données — utilisé pour annuler visuellement une saisie que l'utilisateur a refusé de confirmer. */
  const revertFields = () => setReloadNonce((n) => n + 1);

  /**
   * Accepte aussi bien la valeur ISO d'un sélecteur natif que du texte tapé
   * librement (AAAA-MM-JJ, JJ/MM/AAAA, ou JJ/MM sans année — complétée par
   * l'année en cours, voir utils/dateNormalize). Une saisie vide efface la
   * date ; une saisie non vide mais non reconnue est refusée (alerte) plutôt
   * qu'silencieusement ignorée ou envoyée telle quelle au serveur.
   */
  const normalizeOrReject = (rawValue: string): string | null | undefined => {
    if (rawValue.trim() === '') return null;
    const normalized = normalizeDateToken(rawValue, dateFormat);
    if (!normalized) {
      alert(t('tasks.invalidDate', { value: rawValue, format: dateFormat }) as string);
      revertFields();
      return undefined;
    }
    return fromDateInput(normalized);
  };

  const handleStartDateBlur = (task: Task, rawValue: string) => {
    const value = normalizeOrReject(rawValue);
    if (value === undefined) return; // saisie invalide, déjà signalée
    if (value === task.startDate) return;
    if (task.predecessorsText) {
      const proceed = confirm(t('tasks.startDateOverrideConfirm', { predecessors: task.predecessorsText }) as string);
      if (!proceed) {
        revertFields();
        return;
      }
      patchTask(task.id, { startDate: value, predecessorsText: '' });
      return;
    }
    patchTask(task.id, { startDate: value });
  };

  const handleEndDateBlur = (task: Task, rawValue: string) => {
    const value = normalizeOrReject(rawValue);
    if (value === undefined) return; // saisie invalide, déjà signalée
    if (value !== task.endDate) patchTask(task.id, { endDate: value });
  };

  // Profondeur de chaque tâche à partir de sa chaîne de parentId — sert
  // uniquement à l'indentation visuelle, jamais à l'ordre des lignes (qui
  // reste piloté par `position`/le tri déjà appliqué côté serveur).
  const depthById = useMemo(() => {
    const byId = new Map(tasks.map((t) => [t.id, t]));
    const cache = new Map<string, number>();
    const depthOf = (id: string): number => {
      if (cache.has(id)) return cache.get(id)!;
      const parentId = byId.get(id)?.parentId ?? null;
      const d = parentId ? depthOf(parentId) + 1 : 0;
      cache.set(id, d);
      return d;
    };
    for (const t of tasks) depthOf(t.id);
    return cache;
  }, [tasks]);

  const handleIndent = async (id: string) => {
    pushHistory();
    beginSave();
    try {
      await api.patch(`/tasks/${id}/indent`);
      load();
      markSaved();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setError(message);
      alert(message);
    } finally {
      endSave();
    }
  };

  const handleOutdent = async (id: string) => {
    pushHistory();
    beginSave();
    try {
      await api.patch(`/tasks/${id}/outdent`);
      load();
      markSaved();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setError(message);
      alert(message);
    } finally {
      endSave();
    }
  };

  const selectCell = (taskId: string, column: string) => {
    if (!canWrite) return;
    setFormatSelection({ type: 'cell', taskId, column });
  };

  const selectRow = (taskId: string) => {
    if (!canWrite) return;
    setFormatSelection({ type: 'row', taskId });
  };

  const selectedTask = formatSelection ? tasks.find((t) => t.id === formatSelection.taskId) : undefined;
  const selectedStyle: CellStyle =
    !selectedTask || !formatSelection
      ? {}
      : formatSelection.type === 'row'
        ? { ...selectedTask.formatting?.row }
        : mergeCellStyle(selectedTask.formatting, formatSelection.column);

  const applyFormatting = async (next: TaskFormatting) => {
    if (!selectedTask) return;
    pushHistory();
    beginSave();
    try {
      await api.patch(`/tasks/${selectedTask.id}/format`, { formatting: Object.keys(next).length ? next : null });
      load();
      markSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      endSave();
    }
  };

  const handleFormatChange = (patch: Partial<CellStyle>) => {
    if (!selectedTask || !formatSelection) return;
    const nextStyle: CellStyle = { ...selectedStyle, ...patch };
    for (const key of Object.keys(nextStyle) as (keyof CellStyle)[]) {
      if (nextStyle[key] === undefined) delete nextStyle[key];
    }
    const next: TaskFormatting = { ...selectedTask.formatting };
    if (formatSelection.type === 'row') {
      if (Object.keys(nextStyle).length) next.row = nextStyle;
      else delete next.row;
    } else {
      next.cells = { ...next.cells };
      if (Object.keys(nextStyle).length) next.cells[formatSelection.column] = nextStyle;
      else delete next.cells[formatSelection.column];
      if (!Object.keys(next.cells).length) delete next.cells;
    }
    applyFormatting(next);
  };

  const handleFormatClear = () => {
    if (!selectedTask || !formatSelection) return;
    const next: TaskFormatting = { ...selectedTask.formatting };
    if (formatSelection.type === 'row') {
      delete next.row;
    } else {
      next.cells = { ...next.cells };
      delete next.cells[formatSelection.column];
      if (!Object.keys(next.cells).length) delete next.cells;
    }
    applyFormatting(next);
  };

  const handleDropOnRow = async (index: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === index) return;
    pushHistory();
    const next = [...tasks];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    setTasks(next);
    try {
      await api.patch(`/diagrams/${diagramId}/tasks/reorder`, { orderedIds: next.map((t) => t.id) });
      load();
      markSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      load();
    }
  };

  return (
    <div>
      {error && <p className="error-text">{error}</p>}

      {canWrite && (
        <div className="toolbar">
          <button className="btn" onClick={() => handleAdd()}>
            {t('tasks.newTask')}
          </button>
          <button className="btn" disabled={selected.size === 0} onClick={handleDuplicateSelected}>
            {t('tasks.duplicateSelected', { count: selected.size })}
          </button>
          <button className="btn btn-danger" disabled={selected.size === 0} onClick={handleDeleteSelected}>
            {t('tasks.deleteSelected', { count: selected.size })}
          </button>
          <button className="btn" disabled={historyLength === 0} onClick={handleUndo} title={t('tasks.undoHint') as string}>
            ↩ {t('tasks.undo')}
          </button>
        </div>
      )}

      {canWrite && formatSelection && selectedTask && (
        <FormatToolbar
          label={
            formatSelection.type === 'row'
              ? (t('format.rowLabel', { number: selectedTask.number }) as string)
              : (t('format.cellLabel', { number: selectedTask.number, column: t(`tasks.colShort.${formatSelection.column}`) }) as string)
          }
          style={selectedStyle}
          onChange={handleFormatChange}
          onClear={handleFormatClear}
          onClose={() => setFormatSelection(null)}
        />
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="task-table">
          <colgroup>
            {canWrite && <col style={{ width: 26 }} />}
            <col style={{ width: 32 }} />
            {canWrite && <col style={{ width: 52 }} />}
            <col style={{ width: colWidths.nameFr }} />
            <col style={{ width: colWidths.nameEn }} />
            <col style={{ width: colWidths.duration }} />
            <col style={{ width: colWidths.start }} />
            <col style={{ width: colWidths.end }} />
            <col style={{ width: colWidths.predecessors }} />
            <col style={{ width: colWidths.progress }} />
            <col style={{ width: colWidths.milestone }} />
            <col style={{ width: colWidths.diagAuto }} />
            <col style={{ width: colWidths.visible }} />
            <col style={{ width: colWidths.master }} />
            {canWrite && <col style={{ width: 56 }} />}
          </colgroup>
          <thead>
            <tr>
              {canWrite && <th></th>}
              <th>#</th>
              {canWrite && <th></th>}
              <th>
                {t('tasks.nameFr')}
                <ColumnResizeHandle onResize={(d) => resizeCol('nameFr', d)} />
              </th>
              <th>
                {t('tasks.nameEn')}
                <ColumnResizeHandle onResize={(d) => resizeCol('nameEn', d)} />
              </th>
              <th>
                {t('tasks.duration')}
                <ColumnResizeHandle onResize={(d) => resizeCol('duration', d)} />
              </th>
              <th>
                {t('tasks.startDate')}
                <ColumnResizeHandle onResize={(d) => resizeCol('start', d)} />
              </th>
              <th>
                {t('tasks.endDate')}
                <ColumnResizeHandle onResize={(d) => resizeCol('end', d)} />
              </th>
              <th>
                {t('tasks.predecessors')}
                <ColumnResizeHandle onResize={(d) => resizeCol('predecessors', d)} />
              </th>
              <th>
                %<ColumnResizeHandle onResize={(d) => resizeCol('progress', d)} />
              </th>
              <th>
                {t('tasks.clientMilestone')}
                <ColumnResizeHandle onResize={(d) => resizeCol('milestone', d)} />
              </th>
              <th>
                Diagauto
                <ColumnResizeHandle onResize={(d) => resizeCol('diagAuto', d)} />
              </th>
              <th>
                {t('tasks.visible')}
                <ColumnResizeHandle onResize={(d) => resizeCol('visible', d)} />
              </th>
              <th>
                {t('tasks.master')}
                <ColumnResizeHandle onResize={(d) => resizeCol('master', d)} />
              </th>
              {canWrite && <th></th>}
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, index) => {
              return (
                <tr
                  key={task.id}
                  draggable={canWrite}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => canWrite && e.preventDefault()}
                  onDrop={() => canWrite && handleDropOnRow(index)}
                  style={{ opacity: task.visible ? 1 : 0.5, cursor: canWrite ? 'grab' : 'default' }}
                >
                  {canWrite && (
                    <td>
                      <input type="checkbox" checked={selected.has(task.id)} onChange={() => toggleSelected(task.id)} />
                    </td>
                  )}
                  <td
                    onClick={() => selectRow(task.id)}
                    title={canWrite ? (t('format.selectRowHint') as string) : undefined}
                    style={{ cursor: canWrite ? 'pointer' : undefined, ...cellStyleToCss(task.formatting?.row ?? {}) }}
                  >
                    {task.number}
                  </td>
                  {canWrite && (
                    <td>
                      <div className="toolbar" style={{ flexWrap: 'nowrap', margin: 0, gap: 2 }}>
                        <button
                          className="btn"
                          style={{ padding: '1px 5px' }}
                          title={t('tasks.outdent') as string}
                          disabled={!task.parentId}
                          onClick={() => handleOutdent(task.id)}
                        >
                          ←
                        </button>
                        <button
                          className="btn"
                          style={{ padding: '1px 5px' }}
                          title={
                            index > 0 && indentBlockedReason(tasks[index - 1])
                              ? (t('tasks.indentBlockedHint', {
                                  number: tasks[index - 1].number,
                                  name: tasks[index - 1].nameFr || '—',
                                }) as string)
                              : (t('tasks.indent') as string)
                          }
                          disabled={index === 0 || !!indentBlockedReason(tasks[index - 1])}
                          onClick={() => handleIndent(task.id)}
                        >
                          →
                        </button>
                      </div>
                    </td>
                  )}
                  <td onClick={() => selectCell(task.id, 'nameFr')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'nameFr'))}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: (depthById.get(task.id) ?? 0) * 14 }}>
                      {task.isGroup && <span title={t('tasks.groupHint') as string}>📁</span>}
                      <input
                        key={`nameFr-${task.nameFr}-${reloadNonce}`}
                        defaultValue={task.nameFr}
                        disabled={!canWrite}
                        style={task.isGroup ? { fontWeight: 600 } : undefined}
                        onBlur={(e) => e.target.value !== task.nameFr && patchTask(task.id, { nameFr: e.target.value })}
                      />
                    </div>
                  </td>
                  <td onClick={() => selectCell(task.id, 'nameEn')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'nameEn'))}>
                    <input
                      key={`nameEn-${task.nameEn}-${reloadNonce}`}
                      defaultValue={task.nameEn}
                      disabled={!canWrite}
                      onBlur={(e) => e.target.value !== task.nameEn && patchTask(task.id, { nameEn: e.target.value })}
                    />
                  </td>
                  <td onClick={() => selectCell(task.id, 'duration')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'duration'))}>
                    <input
                      key={`duration-${task.durationDays}-${reloadNonce}`}
                      type="number"
                      min={0}
                      defaultValue={task.durationDays ?? ''}
                      disabled={!canWrite || task.isGroup}
                      title={task.isGroup ? (t('tasks.groupDatesHint') as string) : (t('tasks.durationHint') as string)}
                      onBlur={(e) => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value);
                        if (value !== task.durationDays) patchTask(task.id, { durationDays: value });
                      }}
                    />
                  </td>
                  <td onClick={() => selectCell(task.id, 'start')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'start'))}>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <input
                        key={`start-${task.startDate}-${reloadNonce}`}
                        type="text"
                        placeholder={dateFormat.toLowerCase()}
                        defaultValue={formatDate(task.startDate, dateFormat)}
                        disabled={!canWrite || task.isGroup}
                        title={
                          task.isGroup
                            ? (t('tasks.groupDatesHint') as string)
                            : task.predecessorsText
                              ? (t('tasks.startDateFromPredecessor') as string)
                              : (t('tasks.dateEntryHint', { format: dateFormat }) as string)
                        }
                        onBlur={(e) => handleStartDateBlur(task, e.target.value)}
                      />
                      <input
                        type="date"
                        className="date-picker-btn"
                        value=""
                        disabled={!canWrite || task.isGroup}
                        title={t('tasks.pickDate') as string}
                        tabIndex={-1}
                        onChange={(e) => e.target.value && handleStartDateBlur(task, e.target.value)}
                      />
                    </div>
                  </td>
                  <td onClick={() => selectCell(task.id, 'end')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'end'))}>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <input
                        key={`end-${task.endDate}-${reloadNonce}`}
                        type="text"
                        placeholder={dateFormat.toLowerCase()}
                        defaultValue={formatDate(task.endDate, dateFormat)}
                        disabled={!canWrite || task.isGroup}
                        title={task.isGroup ? (t('tasks.groupDatesHint') as string) : (t('tasks.dateEntryHint', { format: dateFormat }) as string)}
                        onBlur={(e) => handleEndDateBlur(task, e.target.value)}
                      />
                      <input
                        type="date"
                        className="date-picker-btn"
                        value=""
                        disabled={!canWrite || task.isGroup}
                        title={t('tasks.pickDate') as string}
                        tabIndex={-1}
                        onChange={(e) => e.target.value && handleEndDateBlur(task, e.target.value)}
                      />
                    </div>
                  </td>
                  <td onClick={() => selectCell(task.id, 'predecessors')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'predecessors'))}>
                    <input
                      key={`pred-${task.predecessorsText}-${reloadNonce}`}
                      defaultValue={task.predecessorsText}
                      disabled={!canWrite || task.isGroup}
                      title={task.isGroup ? (t('tasks.groupNoPredecessorHint') as string) : (t('tasks.predecessorsHint') as string)}
                      onDoubleClick={() => canWrite && !task.isGroup && setEditingPredecessorsOf(task)}
                      onBlur={(e) =>
                        e.target.value !== task.predecessorsText && patchTask(task.id, { predecessorsText: e.target.value })
                      }
                    />
                  </td>
                  <td onClick={() => selectCell(task.id, 'progress')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'progress'))}>
                    <input
                      key={`progress-${task.progressPct}-${reloadNonce}`}
                      type="number"
                      min={0}
                      max={100}
                      defaultValue={task.progressPct}
                      disabled={!canWrite || task.isGroup}
                      title={task.isGroup ? (t('tasks.groupProgressHint') as string) : undefined}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value || '0', 10);
                        if (value !== task.progressPct) patchTask(task.id, { progressPct: value });
                      }}
                    />
                  </td>
                  <td
                    onClick={() => selectCell(task.id, 'milestone')}
                    style={{ textAlign: 'center', ...cellStyleToCss(mergeCellStyle(task.formatting, 'milestone')) }}
                  >
                    <input
                      type="checkbox"
                      checked={task.isClientMilestone}
                      disabled={!canWrite}
                      onChange={(e) => patchTask(task.id, { isClientMilestone: e.target.checked })}
                    />
                  </td>
                  <td onClick={() => selectCell(task.id, 'diagAuto')} style={cellStyleToCss(mergeCellStyle(task.formatting, 'diagAuto'))}>
                    <select
                      value={task.diagAuto ?? ''}
                      disabled={!canWrite}
                      onChange={(e) => patchTask(task.id, { diagAuto: (e.target.value || null) as DiagAutoType | null })}
                    >
                      <option value=""></option>
                      <option value="LOG">LOG</option>
                      <option value="CHA">CHA</option>
                      <option value="DES">DES</option>
                      <option value="FAC">FAC</option>
                      <option value="COM">COM</option>
                    </select>
                  </td>
                  <td
                    onClick={() => selectCell(task.id, 'visible')}
                    style={{ textAlign: 'center', ...cellStyleToCss(mergeCellStyle(task.formatting, 'visible')) }}
                  >
                    <input
                      type="checkbox"
                      checked={task.visible}
                      disabled={!canWrite}
                      onChange={(e) => patchTask(task.id, { visible: e.target.checked })}
                    />
                  </td>
                  <td
                    onClick={() => selectCell(task.id, 'master')}
                    style={{ textAlign: 'center', ...cellStyleToCss(mergeCellStyle(task.formatting, 'master')) }}
                  >
                    <input
                      type="checkbox"
                      checked={task.isMaster}
                      disabled={!canWrite}
                      onChange={(e) => patchTask(task.id, { isMaster: e.target.checked })}
                    />
                  </td>
                  {canWrite && (
                    <td>
                      <div className="toolbar" style={{ flexWrap: 'nowrap', margin: 0, gap: 2 }}>
                        <button className="btn" style={{ padding: '1px 5px' }} title={t('tasks.insertAfter') as string} onClick={() => handleAdd(task.id)}>
                          +
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '1px 5px' }}
                          title={t('folders.delete') as string}
                          onClick={async () => {
                            if (task.isGroup && !confirm(t('tasks.deleteGroupConfirm') as string)) return;
                            pushHistory();
                            await api.delete(`/tasks/${task.id}`);
                            load();
                            markSaved();
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingPredecessorsOf && (
        <PredecessorEditorModal
          task={editingPredecessorsOf}
          allTasks={tasks}
          onClose={() => setEditingPredecessorsOf(null)}
          onSave={async (text) => {
            await patchTask(editingPredecessorsOf.id, { predecessorsText: text });
            setEditingPredecessorsOf(null);
          }}
        />
      )}
    </div>
  );
}
